import React, { useEffect } from 'react';
import BlogLayout from '../../components/BlogLayout';

export default function AiEmailInfrastructure() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <BlogLayout
            title="How Our AI Agent Built Its Own Email Infrastructure"
            date="March 3, 2026"
            readTime="4 MIN"
            heroImage="/images/blog/ai-email-infrastructure-hero.png"
            description="This morning, our RoofClaw sales agent identified a problem with its own email setup and fixed it autonomously. Here's what happened and why it matters."
        >
            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                This morning, our RoofClaw sales agent — Vito — identified a problem with its own email setup and fixed it. Autonomously.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                I gave it two API keys. That was my entire contribution to the technical side.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Here's what actually happened, why it matters, and what it tells us about where AI is going for small businesses.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">THE PROBLEM NOBODY TALKS ABOUT: POLLING VS. PUSH</h2>
            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                When we first set up Vito's email monitoring, we did what most AI systems do: built a cron job that checked the inbox every 15 minutes.
            </p>
            <p className="mb-4 text-[#F0EFF4]/80 text-lg leading-relaxed">
                It worked. But it had real problems:
            </p>
            <ul className="list-disc pl-6 mb-12 text-[#7B61FF] space-y-2">
                <li><span className="text-[#F0EFF4]/80">15-minute lag on client email replies</span></li>
                <li><span className="text-[#F0EFF4]/80">Fragile OAuth tokens that could expire and knock the whole system offline</span></li>
                <li><span className="text-[#F0EFF4]/80">Risk of double-sends if the cron fired twice</span></li>
                <li><span className="text-[#F0EFF4]/80">No real-time awareness — just a robot knocking on a door every quarter hour</span></li>
            </ul>
            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed font-semibold italic">
                For a product we sell as "your AI employee," this was a gap.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">THE QUESTION THAT CHANGED EVERYTHING</h2>
            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                During a morning strategy conversation, we asked a simple question: what if the agent had its own dedicated email infrastructure — built for AI, not humans?
            </p>
            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Not a Gmail account. Not a shared inbox. A purpose-built system where emails PUSH to the agent in real-time, where the agent has full programmatic control, and where the infrastructure is isolated from our human email stack.
            </p>
            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That led us to AgentMail (agentmail.to) — an API-first email platform designed specifically for AI agents.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">WHAT VITO DID (WITHOUT US)</h2>
            <div className="space-y-6 mb-8">
                <div className="bg-[#0A0A14] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="text-[#F0EFF4]/80 text-lg">
                        <span className="text-[#7B61FF] font-mono mr-4 font-bold">1.</span>
                        Checked the current DNS state of roofclaw.com via the Vercel API — confirmed zero email records. Clean slate.
                    </p>
                </div>
                <div className="bg-[#0A0A14] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="text-[#F0EFF4]/80 text-lg">
                        <span className="text-[#7B61FF] font-mono mr-4 font-bold">2.</span>
                        Registered roofclaw.com with AgentMail. One API call. AgentMail returned 5 DNS records: MX, DKIM, SPF, DMARC, and a feedback MX for bounces.
                    </p>
                </div>
                <div className="bg-[#0A0A14] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="text-[#F0EFF4]/80 text-lg">
                        <span className="text-[#7B61FF] font-mono mr-4 font-bold">3.</span>
                        Added all 5 records to Vercel DNS directly — no dashboard, no copy-paste. Called the Vercel API 5 times.
                    </p>
                </div>
                <div className="bg-[#0A0A14] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="text-[#F0EFF4]/80 text-lg">
                        <span className="text-[#7B61FF] font-mono mr-4 font-bold">4.</span>
                        Waited for verification. All 5 records came back VALID within 90 seconds. Status: VERIFIED.
                    </p>
                </div>
                <div className="bg-[#0A0A14] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="text-[#F0EFF4]/80 text-lg">
                        <span className="text-[#7B61FF] font-mono mr-4 font-bold">5.</span>
                        Created vito@roofclaw.com, sent a test email to me.
                    </p>
                </div>
            </div>
            <p className="mb-12 text-[#F0EFF4] text-xl leading-relaxed font-bold border-l-4 border-[#7B61FF] pl-4">
                Total time: ~45 minutes. My job: sign up for AgentMail and share two API keys.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">THE TWO-ADDRESS STRATEGY</h2>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="bg-[#0A0A14] border border-white/10 rounded-3xl p-8 hover:border-[#7B61FF]/30 transition-colors flex flex-col items-start justify-start">
                    <h3 className="mb-6 font-mono w-full">
                        <span className="block text-xl text-[#7B61FF] break-words mb-2">vito@roofclaw.com</span>
                        <span className="block text-sm text-[#7B61FF]/70">(AgentMail)</span>
                    </h3>
                    <ul className="space-y-4">
                        <li className="flex gap-3 text-[#F0EFF4]/80"><span className="text-[#7B61FF]">→</span> Client-facing email: onboarding, support, "Stay Excited" sequences</li>
                        <li className="flex gap-3 text-[#F0EFF4]/80"><span className="text-[#7B61FF]">→</span> Push-based — real-time inbox awareness</li>
                        <li className="flex gap-3 text-[#F0EFF4]/80"><span className="text-[#7B61FF]">→</span> Clean brand: clients hear from RoofClaw, not our parent company</li>
                        <li className="flex gap-3 text-[#F0EFF4]/80"><span className="text-[#7B61FF]">→</span> Locked down from a trust perspective: Siloed, all emails treated as no trust, doesn't download things, doesn't expose keys</li>
                    </ul>
                </div>

                <div className="bg-[#0A0A14] border border-white/10 rounded-3xl p-8 hover:border-[#7B61FF]/30 transition-colors flex flex-col items-start justify-start">
                    <h3 className="mb-6 font-mono w-full">
                        <span className="block text-xl text-white/50 break-words mb-2">vitoclawleone@roofingbusinesspartner.com</span>
                        <span className="block text-sm text-white/40">(Google Workspace)</span>
                    </h3>
                    <ul className="space-y-4">
                        <li className="flex gap-3 text-[#F0EFF4]/80"><span className="text-white/50">→</span> Cold outreach to prospects only</li>
                        <li className="flex gap-3 text-[#F0EFF4]/80"><span className="text-white/50">→</span> Established domain reputation for deliverability</li>
                        <li className="flex gap-3 text-[#F0EFF4]/80"><span className="text-white/50">→</span> Same Lockdown from a trust perspective: Treats every email as malicious FIRST, and safely proves an email is safe before interacting. Hardened for prompt injection.</li>
                    </ul>
                </div>
            </div>
            <p className="mb-12 text-[#F0EFF4]/90 text-xl font-medium tracking-wide text-center uppercase">
                Right tool for the right job.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">WHY THIS MATTERS BEYOND EMAIL</h2>
            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                We're at the beginning of a shift where AI agents don't just use the tools you give them — they acquire the tools they need.
            </p>
            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Vito identified a gap, researched solutions, evaluated tradeoffs, asked for credentials, and executed the implementation.
            </p>
            <p className="mb-12 text-[#F0EFF4] text-2xl font-serif italic text-center px-8 border-y border-white/10 py-8">
                "That's not a chatbot. That's closer to an employee who notices a problem Monday morning and has it solved by Tuesday."
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">WHAT THIS MEANS FOR ROOFCLAW CLIENTS</h2>
            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Every RoofClaw we ship comes with a dedicated AI employee — and now, that employee has proper email infrastructure to match.
            </p>
            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                For clients where email is the primary business channel (we're already building for auto dealerships chasing lender stips), this isn't optional. It's the foundation.
            </p>
            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                We're still in early days. But mornings like this one — where the agent solves its own problems — are exactly what we're building toward.
            </p>

            <div className="bg-[#7B61FF]/10 rounded-3xl p-8 border border-[#7B61FF]/30 mt-16 text-center">
                <h3 className="text-2xl font-bold font-heading text-white mb-4">Ready for your own AI employee?</h3>
                <p className="text-[#F0EFF4]/80 mb-6 max-w-2xl mx-auto">
                    RoofClaw is a fully-configured AI business assistant running on dedicated Apple hardware. Built for roofing businesses, expanding to other trades.
                </p>
                <a href="/" className="inline-block bg-[#7B61FF] text-white px-8 py-4 rounded-full font-bold hover:scale-105 transition-transform">
                    Explore RoofClaw
                </a>
            </div>

        </BlogLayout>
    );
}
